'use client';
// Shared MQTT client + ref-counted subscription dispatcher.
//
// All MQTT-using pages in the app go through this one provider:
//   - app/components/ModbusData.tsx (binary modbus frames per inverter)
//   - app/site/modbus/[site_id]/page.tsx (debug page; subscribes to '#')
//
// The provider holds the mqtt.connect lifecycle. The actual subscription
// bookkeeping and message dispatch live in MqttSubscriber (a pure class,
// see app/utils/mqttSubscriber.ts) so they can be unit-tested without
// jsdom or a real broker.
//
// Lifecycle:
//   1. A child component calls setCredentials(...) once it knows the site's
//      MQTT user / password / clientId. Before that, the provider is idle —
//      no socket is opened.
//   2. The provider connects on the first credential, wires the lifecycle
//      events, and forwards 'message' into MqttSubscriber.dispatch.
//   3. On (re)connect, MqttSubscriber.resubscribeAll replays every active
//      pattern to the broker — brokers do not preserve subscriptions across
//      a disconnected session by default.
//   4. Credential change → the existing client is torn down and a new one
//      is created. resubscribeAll restores subscriptions on the new client.
//   5. Provider unmount → client.end(true), subscriber is detached.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import mqtt, { MqttClient } from 'mqtt';
import { MqttSubscriber, MqttMessageHandler } from '@/app/utils/mqttSubscriber';
import { acquireTabLock } from '@/app/utils/tabLock';
import { makeSessionSuffix, withSessionSuffix } from '@/app/utils/mqttClientId';

export type MqttCredentials = {
  user: string;
  password: string;
  /** Stable, site-scoped base clientId (e.g. `live-<site.name>`). Callers
   *  pass the *base* — the provider appends a per-browser-context suffix
   *  before connecting so two independent browser contexts (other profile,
   *  device, incognito, automation) never collide on the broker. Keep this
   *  base stable across a tab's lifetime so the provider dedupes a page and
   *  its useLivePower hook onto one session. See app/utils/mqttClientId.ts. */
  clientId: string;
  /** Optional site identifier. Not used for the connection itself — it's a
   *  hint for change-detection and logs so a user with access to multiple
   *  sites gets a clean reconnect when switching between them. */
  siteId?: number | string;
};

export type MqttStatus =
  | 'idle'           // no credentials yet
  | 'connecting'     // socket open, awaiting CONNACK
  | 'connected'
  | 'disconnected'
  | 'error'
  | 'blocked';       // another browser tab holds the single-active lock

/** Name of the Web Lock used to enforce single-active-tab. Exported so
 *  tests can pass a custom name to a custom provider; in production this
 *  is the only name we use. */
export const MQTT_TAB_LOCK = 'powston-mqtt-connection';

export type MqttContextValue = {
  status: MqttStatus;
  /** Push credentials in. Passing `null` tears down the current connection. */
  setCredentials: (creds: MqttCredentials | null) => void;
  /** Register a handler for a topic pattern. Returns an unsubscribe fn.
   *  Ref-counted — multiple subscribers to the same pattern share one MQTT
   *  SUBSCRIBE. */
  subscribe: (pattern: string, handler: MqttMessageHandler) => () => void;
  /** Publish a message. No-op if there's no active client. */
  publish: (topic: string, payload: string | Buffer) => void;
};

const Ctx = createContext<MqttContextValue | null>(null);

const DEFAULT_BROKER_URL = 'wss://ws.powston.com:8083';

type MqttProviderProps = {
  children: React.ReactNode;
  /** Override the broker URL (mostly useful for local testing). */
  url?: string;
};

export function MqttProvider({ children, url = DEFAULT_BROKER_URL }: MqttProviderProps) {
  const [status, setStatus] = useState<MqttStatus>('idle');
  const [creds, setCreds] = useState<MqttCredentials | null>(null);

  // The subscriber survives client churn — we hand it the new client on
  // each connect cycle so registered handlers carry over.
  const subscriberRef = useRef<MqttSubscriber | null>(null);
  if (subscriberRef.current === null) {
    subscriberRef.current = new MqttSubscriber();
  }

  const clientRef = useRef<MqttClient | null>(null);

  // Random suffix identifying this browser context, generated once and
  // reused for every connect cycle. Appending it to the caller's stable
  // base clientId makes the broker clientId unique per context, so a site
  // opened in a second browser/profile/device no longer evicts this one in
  // a connect/1006-close loop. Stable across credential changes (site
  // switches) — still unique per context, which is all the broker needs.
  const sessionSuffixRef = useRef<string | null>(null);
  if (sessionSuffixRef.current === null) {
    sessionSuffixRef.current = makeSessionSuffix();
  }

  useEffect(() => {
    // SSR guard — `mqtt` reaches for WebSocket which doesn't exist on the
    // server. The component is `'use client'` already, but the effect body
    // is still nominally callable in a Node test runner.
    if (typeof window === 'undefined') return;
    if (!creds) {
      setStatus('idle');
      return;
    }

    setStatus('connecting');

    // Single-active-tab gate. The browser releases the Web Lock on tab
    // close, so the queued tab auto-recovers. If navigator.locks isn't
    // available (very old browser), fall through to no enforcement.
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    let cancelled = false;
    let activeClient: MqttClient | null = null;
    let activeRelease: (() => void) | null = null;

    const startConnection = () => {
      if (cancelled) return;
      setStatus('connecting');
      const client = mqtt.connect(url, {
        username: creds.user,
        password: creds.password,
        protocol: 'wss',
        keepalive: 60,
        reconnectPeriod: 5000,
        clientId: withSessionSuffix(creds.clientId, sessionSuffixRef.current!),
      });
      activeClient = client;
      clientRef.current = client;
      subscriberRef.current!.setClient(client);

      client.on('connect', () => {
        setStatus('connected');
        subscriberRef.current!.resubscribeAll();
      });
      client.on('reconnect', () => {
        setStatus('connecting');
        subscriberRef.current!.resubscribeAll();
      });
      client.on('message', (topic, payload, packet) => {
        // mqtt.js passes the full IPublishPacket as the 3rd arg. We only
        // need `retain` downstream so we narrow it; if mqtt.js ever shifts
        // signatures, the narrow type catches the regression.
        subscriberRef.current!.dispatch(topic, payload as Buffer, {
          retain: packet?.retain,
        });
      });
      client.on('error', (err) => {
        // eslint-disable-next-line no-console
        console.error('MQTT client error:', err);
        setStatus('error');
      });
      client.on('close', () => {
        setStatus('disconnected');
      });
    };

    if (locks) {
      const tabLock = acquireTabLock(locks, MQTT_TAB_LOCK);
      activeRelease = tabLock.release;

      // Show 'blocked' as soon as we know someone else holds the lock,
      // rather than sitting on 'connecting' indefinitely.
      tabLock.initiallyBlocked.then((blocked) => {
        if (cancelled) return;
        if (blocked) setStatus('blocked');
      });

      tabLock.acquired.then(() => {
        if (cancelled) return;
        startConnection();
      });
    } else {
      // No Web Locks API — skip enforcement and connect immediately.
      startConnection();
    }

    return () => {
      cancelled = true;
      subscriberRef.current!.setClient(null);
      if (activeClient) {
        activeClient.end(true);
        clientRef.current = null;
      }
      if (activeRelease) activeRelease();
    };
  }, [creds, url]);

  const setCredentials = useCallback((c: MqttCredentials | null) => {
    setCreds((prev) => {
      // Cheap equality check — avoids unnecessary reconnect when child
      // components re-derive creds from props on every render. siteId is
      // included so a user with two sites in the same tab triggers a
      // clean reconnect when navigating between them, even if (somehow)
      // the broker auth happens to match.
      if (!prev && !c) return prev;
      if (
        prev &&
        c &&
        prev.user === c.user &&
        prev.password === c.password &&
        prev.clientId === c.clientId &&
        prev.siteId === c.siteId
      ) {
        return prev;
      }
      return c;
    });
  }, []);

  const subscribe = useCallback<MqttContextValue['subscribe']>(
    (pattern, handler) => subscriberRef.current!.subscribe(pattern, handler),
    [],
  );

  const publish = useCallback<MqttContextValue['publish']>(
    (topic, payload) => {
      const client = clientRef.current;
      if (!client) return;
      // `mqtt`'s publish signature accepts string|Buffer for the payload —
      // the cast is just to satisfy the union with optional callback args.
      client.publish(topic, payload as Buffer | string);
    },
    [],
  );

  const value = useMemo<MqttContextValue>(
    () => ({ status, setCredentials, subscribe, publish }),
    [status, setCredentials, subscribe, publish],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMqtt(): MqttContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useMqtt must be used inside <MqttProvider>');
  return ctx;
}
